import { collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where, deleteDoc, DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Category } from '@/types/category';
import { SOP } from '@/types/sop';

class FavoritesService {
  private categoriesCollection = collection(db, 'sopCategories');

  async getCategories(userId: string): Promise<Category[]> {
    try {
      const q = query(this.categoriesCollection, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as Category));
    } catch (error) {
      console.error('Error getting categories:', error);
      throw error;
    }
  }

  async getPublicCategories(): Promise<Category[]> {
    try {
      const q = query(this.categoriesCollection, where('isPublic', '==', true));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as Category));
    } catch (error) {
      console.error('Error getting public categories:', error);
      throw error;
    }
  }

  async createCategory(
    name: string,
    userId: string,
    displayName?: string,
    profilePic?: string
  ): Promise<Category> {
    try {
      const categoryRef = doc(this.categoriesCollection);
      const createdAt = new Date();

      const categoryData = {
        name,
        userId,
        sopIds: [],
        createdAt,
        isPublic: false
      };

      await setDoc(categoryRef, categoryData);

      return { ...categoryData, id: categoryRef.id };
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  async addSOPToCategory(sopId: string, categoryId: string, sop: SOP): Promise<void> {
    try {
      const categoryRef = doc(this.categoriesCollection, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      
      if (categoryDoc.exists()) {
        const category = categoryDoc.data() as Category;
        if (!category.sopIds.includes(sopId)) {
          await updateDoc(categoryRef, {
            sopIds: [...category.sopIds, sopId]
          });
        }
      }
    } catch (error) {
      console.error('Error adding SOP to category:', error);
      throw error;
    }
  }

  async removeSOPFromCategory(sopId: string, categoryId: string): Promise<void> {
    try {
      const categoryRef = doc(this.categoriesCollection, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      
      if (categoryDoc.exists()) {
        const category = categoryDoc.data() as Category;
        await updateDoc(categoryRef, {
          sopIds: category.sopIds.filter(id => id !== sopId)
        });
      }
    } catch (error) {
      console.error('Error removing SOP from category:', error);
      throw error;
    }
  }

  async updateCategoryVisibility(
    categoryId: string,
    isPublic: boolean,
    userName: string,
    profilePic?: string
  ): Promise<void> {
    try {
      const categoryRef = doc(this.categoriesCollection, categoryId);
      const categoryDoc = await getDoc(categoryRef);

      if (!categoryDoc.exists()) {
        throw new Error('Category not found');
      }

      const category = categoryDoc.data() as Category;
      await updateDoc(categoryRef, {
        isPublic,
        // Store creator info for public display
        creatorInfo: isPublic ? {
          name: userName,
          displayName: userName,
          profilePic
        } : null
      });
    } catch (error) {
      console.error('Error updating category visibility:', error);
      throw error;
    }
  }

  async importCategory(
    categoryId: string,
    userId: string,
    userName: string,
    profilePic?: string
  ): Promise<void> {
    try {
      // Get the original category
      const originalCategoryDoc = await getDoc(doc(this.categoriesCollection, categoryId));
      if (!originalCategoryDoc.exists()) {
        throw new Error('Category not found');
      }
      
      const originalCategory = originalCategoryDoc.data() as Category;

      // Create a new category for the importing user
      const newCategoryRef = doc(this.categoriesCollection);
      const createdAt = new Date();
      
      // Use the creator's info from the source category
      const creatorInfo = originalCategory.creatorInfo || {
        name: 'Unknown User',
        displayName: 'Unknown User',
        profilePic: undefined
      };
      
      await setDoc(newCategoryRef, {
        name: originalCategory.name,
        userId,
        sopIds: originalCategory.sopIds,
        createdAt,
        isPublic: false,
        // Set originalOwner to the creator's info
        originalOwner: {
          userId: originalCategory.userId,
          name: creatorInfo.name,
          displayName: creatorInfo.displayName,
          profilePic: creatorInfo.profilePic
        }
      });
    } catch (error) {
      console.error('Error importing category:', error);
      throw error;
    }
  }

  async deleteCategory(categoryId: string): Promise<void> {
    try {
      const categoryRef = doc(this.categoriesCollection, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      
      if (!categoryDoc.exists()) {
        throw new Error('Category not found');
      }
      
      const category = categoryDoc.data() as Category;
      if (category.name === 'My Favorites') {
        throw new Error('Cannot delete the default "My Favorites" category');
      }
      
      await deleteDoc(categoryRef);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  async updateCategoryName(categoryId: string, newName: string): Promise<void> {
    try {
      const categoryRef = doc(this.categoriesCollection, categoryId);
      const categoryDoc = await getDoc(categoryRef);

      if (!categoryDoc.exists()) {
        throw new Error('Category not found');
      }

      const category = categoryDoc.data() as Category;
      if (category.name === 'My Favorites') {
        // Optionally, throw an error or simply log and return
        console.warn('Attempted to rename the "My Favorites" category. This action is not allowed.');
        throw new Error('The "My Favorites" category name cannot be changed.');
      }
      
      if (!newName || newName.trim() === '') {
        throw new Error('Category name cannot be empty.');
      }

      await updateDoc(categoryRef, {
        name: newName
      });
    } catch (error) {
      console.error('Error updating category name:', error);
      throw error;
    }
  }
}

const favoritesService = new FavoritesService();
export default favoritesService;
